import { memo, type ComponentProps } from "react";

import { tw } from "classnames";

export type VideoProps = {
  ariaLabel?: string;
  autoPlay?: boolean;
  controls?: boolean;
  cover?: boolean;
  crossOrigin?: "" | "anonymous" | "use-credentials" | undefined;
  fluid?: boolean;
  height?: number;
  id?: string;
  loop?: boolean;
  muted?: boolean;
  playsInline?: boolean;
  poster?: string;
  preload?: "auto" | "metadata" | "none";
  sources?: { mimeType: string; url: string }[];
  subtitles?: string;
  width?: number;
};

const styles = {
  base: ({ cover, fluid }: { cover?: boolean; fluid?: boolean }) =>
    tw({
      "h-full": cover,
      "object-cover": cover,
      "h-auto": fluid,
      "w-full": cover || fluid,
    }),
};

const VideoBase = ({
  ariaLabel,
  autoPlay,
  controls,
  cover,
  crossOrigin,
  fluid,
  height,
  id,
  loop,
  muted,
  playsInline,
  poster,
  preload = "none",
  sources,
  subtitles,
  width,
  ...passThroughProps
}: ComponentProps<"video"> & VideoProps) =>
  sources && (
    <video
      {...passThroughProps}
      aria-label={ariaLabel}
      autoPlay={autoPlay}
      className={styles.base({ cover, fluid })}
      controls={controls}
      crossOrigin={crossOrigin || (subtitles ? "anonymous" : undefined)}
      height={height}
      id={id}
      loop={loop}
      muted={muted}
      playsInline={playsInline}
      poster={poster}
      preload={preload}
      width={width}
    >
      {sources?.map((source) => (
        <source
          key={source.url}
          src={`${source.url}#t=0.1`}
          type={source.mimeType}
        />
      ))}
      {subtitles && (
        <track
          default
          kind="subtitles"
          label="English"
          src={subtitles}
          srcLang="en"
        />
      )}
    </video>
  );

export const Video = memo(VideoBase);
